/**
 * mwApp.subscribers module <br>
 * Work with subscribers: show/add/edit/remove
 *
 * @namespace mwApp/subscribers
 */

/**
 * ConfigRoutes
 * Configurates module routes
 *
 * @class ConfigRoutes
 * @memberof mwApp/subscribers
 * @see mwApp/subscribers
 *
 * @ngdoc config
 * @name mwApp/subscribers.config:ConfigRoutes
 */
/* @ngInject */
export default function ConfigRoutes ($stateProvider) {
  $stateProvider

    /**
     * abstract state for /subscribers
     */
    .state('app.subscribers', {
      abstract: true,
      url: '/subscribers',
      template: '<ui-view/>'
    })

    /**
     * controller {@link mwApp/subscribers.controller:SubscribersCtrl}
     * @name /subscribers
     * @memberof mwApp/subscribers.config:ConfigRoutes
     */
    .state('app.subscribers.list', {
      url: '',
      templateUrl: 'components/subscribers/views/subscribers.html',
      controller: 'SubscribersCtrl as vm',
      label: 'Subscribers',
      access: 'viewSubscriber'
    })

    /**
     * abstract state for /subscribers/:subscriberId
     */
    .state('app.subscribers.subscriber', {
      abstract: true,
      url: '/:subscriberId',
      template: '<ui-view/>'
    })

    /**
     * controller {@link mwApp/subscribers.controller:AddSubscriberCtrl}
     * @name /subscribers/:subscriberId
     * @memberof mwApp/subscribers.config:ConfigRoutes
     */
    .state('app.subscribers.subscriber.edit', {
      url: '',
      templateUrl: 'components/subscribers/views/addSubscriber.html',
      controller: 'AddSubscriberCtrl as vm',
      label: '{{::subscriberId | addOrEdit}} Subscriber',
      resolve: {
        subscr: /* @ngInject */ function ($transition$, Subscriber) {
          var { subscriberId: id } = $transition$.params()
          return +id
            ? Subscriber.load({ id })
            : new Subscriber()
        },
        extra: /* @ngInject */ function ($transition$, Subscriber) {
          var { subscriberId: id } = $transition$.params()
          return Subscriber.loadExtra({ id: +id || 0 })
        }
      },
      access: ':subscriberId (+) ! IF createSubscriber ELSE editSubscriber editSearch |'
    })

    /**
     * controller {@link mwApp/subscribers.controller:AssignDevicesCtrl}
     * @name /subscribers/:subscriberId/devices
     * @memberof mwApp/subscribers.config:ConfigRoutes
     */
    .state('app.subscribers.subscriber.devices', {
      url: '/devices',
      templateUrl: 'components/subscribers/views/assignDevice.html',
      controller: 'AssignDevicesCtrl as vm',
      label: 'Devices',
      resolve: {
        subscr: /* @ngInject */ function ($transition$, Subscriber) {
          var { subscriberId: id } = $transition$.params()
          return Subscriber.load({
            id,
            params: { withDevices: true }
          })
        },
        extra: /* @ngInject */ function ($transition$, Subscriber) {
          var { subscriberId: id } = $transition$.params()
          return Subscriber.loadExtra({ id: +id || 0, url: '?devices' })
        }
      },
      access: 'editSubscriber editSearch | viewDevice viewSubscriberDevice | &'
    })

    /**
     * controller {@link mwApp/subscribers.controller:CustomStreamsCtrl}
     * @name /subscribers/:subscriberId/customstreams
     * @memberof mwApp/subscribers.config:ConfigRoutes
     */
    .state('app.subscribers.subscriber.customstreams', {
      url: '/customstreams',
      templateUrl: 'components/subscribers/views/customStreams.html',
      controller: 'CustomStreamsCtrl as vm',
      label: _ => _.subscr && _.subscr.type !== 'VOUCHER' ? 'Custom Streams' : null,
      resolve: {
        subscr: /* @ngInject */ function ($transition$, Subscriber) {
          var { subscriberId: id } = $transition$.params()
          return Subscriber.load({
            id
          })
        },
        extra: /* @ngInject */ function ($transition$, Subscriber) {
          var { subscriberId: id } = $transition$.params()
          return Subscriber.loadExtra({ id: +id || 0, url: '?custom' })
        }
      },
      access: 'editSubscriber editSearch | viewSubscriberCustomStream &'
    })

    /**
     * controller {@link mwApp/subscribers.controller:ActivationCtrl}
     * @name /subscribers/:subscriberId/activation
     * @memberof mwApp/subscribers.config:ConfigRoutes
     */
    .state('app.subscribers.subscriber.activation', {
      url: '',
      views: {
        '': {
          templateUrl: 'components/subscribers/activation/views/activation.html',
          controller: 'ActivationCtrl as vm'
        },
        'devices@app.subscribers.subscriber.activation': {
          templateUrl: 'components/subscribers/activation/views/devices.html',
          controller: 'ActivationDevicesCtrl as vm'
        },
        'statements@app.subscribers.subscriber.activation': {
          templateUrl: 'components/subscribers/activation/views/statements.html',
          controller: 'ActivationStatementsCtrl as vm'
        },
        'creditcards@app.subscribers.subscriber.activation': {
          templateUrl: 'components/subscribers/activation/views/creditCards.html',
          controller: 'ActivationCreditCardsCtrl as vm'
        },
        'library@app.subscribers.subscriber.activation': {
          templateUrl: 'components/subscribers/activation/views/library.html',
          controller: 'ActivationLibraryCtrl as vm'
        },
        'librarycontent@app.subscribers.subscriber.activation': {
          templateUrl: 'components/subscribers/activation/views/libraryContent.html',
          controller: 'ActivationLibraryContentCtrl as vm'
        }
      },
      resolve: {
        subscr: /* @ngInject */ function ($transition$, Subscriber) {
          var { subscriberId: id } = $transition$.params()
          return Subscriber.load({
            id,
            params: { withPayments: true }
          })
        },
        extra: /* @ngInject */ function ($transition$, Subscriber) {
          var { subscriberId: id } = $transition$.params()
          return Subscriber.loadExtra({ id: +id || 0, url: '?activation' })
        },
        scope: function () {
          // use object 'scope' to share object between different views
          return {}
        },
        accessories: /* @ngInject */ function (SubscriptionAccessory, subscr) {
          return SubscriptionAccessory.load(`api/networks/${subscr.network.id}/accessories?enabled=true`, SubscriptionAccessory, 'all')
        }

      }
    })

    /**
     * controller {@link mwApp/subscribers.controller:ActivationPaymentsCtrl}
     * @name /subscribers/:subscriberId/activation
     * @memberof mwApp/subscribers.config:ConfigRoutes
     */
    .state('app.subscribers.subscriber.activation.payments', {
      url: '/activation',
      views: {
        '': {
          templateUrl: 'components/subscribers/activation/views/payments.html',
          controller: 'ActivationPaymentsCtrl as vm'
        },
        'Add_Device@app.subscribers.subscriber.activation.payments': {
          templateUrl: 'components/subscribers/activation/views/paymentsAddDevice.html',
          controller: 'ActivationPaymentsAddDeviceCtrl as vm'
        },
        'Common_Subscription@app.subscribers.subscriber.activation.payments': {
          templateUrl: 'components/subscribers/activation/views/paymentsCommonSubscription.html',
          controller: 'ActivationPaymentsCommonSubscriptionCtrl as vm'
        },
        'Custom_Charge@app.subscribers.subscriber.activation.payments': {
          templateUrl: 'components/subscribers/activation/views/paymentsCustomCharge.html',
          controller: 'ActivationPaymentsCustomChargeCtrl as vm'
        },
        'Custom_Subscription@app.subscribers.subscriber.activation.payments': {
          templateUrl: 'components/subscribers/activation/views/paymentsCustomSubscription.html',
          controller: 'ActivationPaymentsCustomSubscriptionCtrl as vm'
        },
        'Content_Addon@app.subscribers.subscriber.activation.payments': {
          templateUrl: 'components/subscribers/activation/views/paymentsContentAddons.html',
          controller: 'ActivationPaymentsContentAddonsCtrl as vm'
        },
        'Misc@app.subscribers.subscriber.activation.payments': {
          templateUrl: 'components/subscribers/activation/views/paymentsMisc.html',
          controller: 'ActivationPaymentsMiscCtrl as vm'
        }
      },
      label: 'Subscription Payments',
      access: 'viewSubscriptionPayment viewGlobalPayment | editSubscriber editSearch | &'
    })

    /**
     * controller {@link mwApp/subscribers.controller:NotesCtrl}
     * @name /subscribers/:subscriberId/notes
     * @memberof mwApp/subscribers.config:ConfigRoutes
     */
    .state('app.subscribers.subscriber.notes', {
      url: '/notes',
      templateUrl: 'components/subscribers/views/notes.html',
      controller: 'NotesCtrl as vm',
      label: 'Notes',
      resolve: {
        subscr: /* @ngInject */ function ($transition$, Subscriber) {
          var { subscriberId: id } = $transition$.params()
          return Subscriber.load({ id })
        },
        extra: /* @ngInject */ function ($transition$, Subscriber) {
          var { subscriberId: id } = $transition$.params()
          return Subscriber.loadExtra({ id: +id || 0, url: '?notes' })
        }
      },
      access: 'viewSubscriberNote editSubscriber editSearch | &'
    })
}
